import { useLayoutEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import logo from '../assets/brit-logo.svg'
import Footer from '../components/Footer'

export default function TermsPage() {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  return (
    <div className='min-h-screen bg-white w-full'>
      {/* Header */}
      <header className='bg-white border-b border-gray-200 sticky top-0 z-10 w-full'>
        <div className='mx-auto px-4 sm:px-6 lg:px-8 py-4 w-full'>
          <div className='flex items-center justify-between w-full'>
            <Link
              to='/'
              className='inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors'
            >
              <ArrowLeft className='w-5 h-5 mr-2' />
              <span className='text-sm font-medium'>Back to Home</span>
            </Link>
            <Link to='/' className='flex items-center'>
              <img src={logo} alt='BritFiler Logo' className='h-8' />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='mx-auto px-4 sm:px-6 lg:px-20 py-8 lg:py-12'>
        <div className='prose prose-lg max-w-none text-left'>
          {/* Page Title */}
          <h1 className='text-4xl font-bold text-gray-900 mb-2'>
            Terms &amp; Policies
          </h1>
          <p className='text-gray-500 text-base mb-10'>
            BritFiler Ltd &mdash; Office 1, Izabella House, 24&ndash;26 Regent Place, Birmingham, B1 3NJ
          </p>

          {/* Quick Navigation */}
          <nav className='bg-gray-50 rounded-xl p-6 mb-12 border border-gray-200'>
            <p className='text-sm font-semibold text-gray-900 mb-3'>Contents</p>
            <ol className='list-decimal pl-5 space-y-1 text-sm'>
              <li><a href='#privacy-policy' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>Privacy Policy</a></li>
              <li><a href='#terms-of-use' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>Terms of Use</a></li>
              <li><a href='#cookie-policy' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>Cookie Policy</a></li>
              <li><a href='#user-agreement' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>User Agreement</a></li>
            </ol>
          </nav>

          {/* ========== 1. PRIVACY POLICY ========== */}
          <section id='privacy-policy' className='mb-16 scroll-mt-24'>
            <div className='flex items-center gap-3 mb-6'>
              <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#051C36] text-white text-sm font-bold shrink-0'>1</span>
              <h2 className='text-2xl font-bold text-gray-900 m-0'>Privacy Policy</h2>
            </div>

            <div className='space-y-8'>
              {/* 1.1 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.1 Introduction</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>
                    This Privacy Policy explains how BritFiler Ltd (&ldquo;BritFiler&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, processes, stores, and protects personal data when you use our software platform and related services (the &ldquo;Services&rdquo;).
                  </p>
                  <p>
                    We are committed to protecting your personal data and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                  </p>
                </div>
              </div>

              {/* 1.2 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.2 Data Controller</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>BritFiler Ltd is the data controller responsible for your personal data.</p>
                  <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm'>
                    <p><strong>Contact Email:</strong>{' '}
                      <a href='mailto:info@britfiler.co.uk' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>info@britfiler.co.uk</a>
                    </p>
                    <p className='mt-1'><strong>Registered Address:</strong> Office 1, Izabella House, 24&ndash;26 Regent Place, Birmingham, B1 3NJ</p>
                  </div>
                </div>
              </div>

              {/* 1.3 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.3 Scope of Services</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>BritFiler provides software for preparing and submitting tax and statutory filings including:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Self Assessment returns</li>
                    <li>Corporation Tax returns (CT600 via XML submission)</li>
                    <li>VAT returns</li>
                    <li>Confirmation Statements</li>
                    <li>Companies House filings</li>
                  </ul>
                  <p>BritFiler acts solely as a technology platform and does not act as a tax agent unless explicitly agreed.</p>
                </div>
              </div>

              {/* 1.4 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.4 Categories of Personal Data</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We may collect and process the following categories of personal data:</p>

                  <div className='grid gap-4 sm:grid-cols-2 text-left'>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(a) Identity Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>Full name</li>
                        <li>Date of birth</li>
                        <li>National Insurance number</li>
                        <li>Unique Taxpayer Reference (UTR)</li>
                      </ul>
                    </div>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(b) Contact Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>Email address</li>
                        <li>Telephone number</li>
                        <li>Residential or business address</li>
                      </ul>
                    </div>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(c) Financial and Tax Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>Income details</li>
                        <li>Business transactions</li>
                        <li>VAT records</li>
                        <li>Payroll data</li>
                        <li>Corporation tax information</li>
                      </ul>
                    </div>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(d) Business Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>Company name</li>
                        <li>Company registration number</li>
                        <li>Director details</li>
                      </ul>
                    </div>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(e) Technical Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>IP address</li>
                        <li>Device type</li>
                        <li>Browser information</li>
                        <li>Operating system</li>
                      </ul>
                    </div>
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                      <p className='font-semibold text-gray-900 mb-2'>(f) Usage Data</p>
                      <ul className='list-disc pl-5 space-y-1 text-sm'>
                        <li>Platform interactions</li>
                        <li>Features used</li>
                        <li>Login activity</li>
                      </ul>
                    </div>
                  </div>

                  <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 text-left'>
                    <p className='font-semibold text-gray-900 mb-2'>(g) Audit and Submission Data</p>
                    <ul className='list-disc pl-5 space-y-1 text-sm'>
                      <li>Filing history</li>
                      <li>HMRC submission logs</li>
                      <li>Timestamp records</li>
                      <li>Error logs</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 1.5 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.5 How We Collect Data</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We collect personal data through:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Direct user input</li>
                    <li>Integration with HMRC and Companies House APIs</li>
                    <li>Cookies and tracking technologies</li>
                    <li>Customer support interactions</li>
                  </ul>
                </div>
              </div>

              {/* 1.6 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.6 Lawful Basis for Processing</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We rely on the following lawful bases:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li><strong>Contract</strong> &ndash; to provide our Services</li>
                    <li><strong>Legal obligation</strong> &ndash; to comply with tax and regulatory requirements</li>
                    <li><strong>Legitimate interests</strong> &ndash; to improve and secure our platform</li>
                    <li><strong>Consent</strong> &ndash; for marketing and non-essential cookies</li>
                  </ul>
                </div>
              </div>

              {/* 1.7 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.7 How We Use Your Data</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We use your personal data to:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Provide tax preparation and submission services</li>
                    <li>Transmit filings to HMRC and Companies House</li>
                    <li>Verify your identity</li>
                    <li>Maintain audit trails</li>
                    <li>Provide customer support</li>
                    <li>Improve system performance</li>
                    <li>Prevent fraud and misuse</li>
                  </ul>
                </div>
              </div>

              {/* 1.8 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.8 HMRC and Companies House Integration</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>BritFiler transmits data via secure APIs. You acknowledge that:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>All submissions are made based on data you provide</li>
                    <li>You are responsible for reviewing submissions before filing</li>
                    <li>BritFiler is not responsible for errors in submitted data</li>
                  </ul>
                </div>
              </div>

              {/* 1.9 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.9 Third-Party Processors</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We use trusted third-party providers including:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Payment processors (e.g., Stripe)</li>
                    <li>Communication services (e.g., Twilio)</li>
                    <li>Cloud infrastructure providers</li>
                    <li>Analytics providers</li>
                  </ul>
                  <p>All third parties process data in compliance with applicable laws.</p>
                </div>
              </div>

              {/* 1.10 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.10 International Transfers</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>Where data is transferred outside the UK, we ensure appropriate safeguards including:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Standard Contractual Clauses (SCCs)</li>
                    <li>Adequacy decisions</li>
                  </ul>
                </div>
              </div>

              {/* 1.11 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.11 Data Security</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We implement appropriate technical and organisational measures, including:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Encryption (in transit and at rest)</li>
                    <li>Role-based access control</li>
                    <li>Multi-factor authentication</li>
                    <li>Continuous monitoring</li>
                    <li>Audit logging</li>
                  </ul>
                </div>
              </div>

              {/* 1.12 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.12 Data Retention</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We retain personal data:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>For at least 6 years in line with HMRC requirements</li>
                    <li>Longer where required by law or for dispute resolution</li>
                  </ul>
                </div>
              </div>

              {/* 1.13 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.13 AI and Automation</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>BritFiler may use AI-assisted tools to support tax calculations and user experience. These tools:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Do not constitute professional advice</li>
                    <li>Do not replace human review</li>
                    <li>Should not be solely relied upon</li>
                  </ul>
                </div>
              </div>

              {/* 1.14 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.14 Your Rights</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>Under UK GDPR, you have the right to:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Access your data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion</li>
                    <li>Restrict processing</li>
                    <li>Data portability</li>
                    <li>Object to processing</li>
                  </ul>
                  <p>
                    To exercise your rights, contact:{' '}
                    <a href='mailto:info@britfiler.co.uk' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>info@britfiler.co.uk</a>
                  </p>
                </div>
              </div>

              {/* 1.15 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.15 Complaints</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You have the right to lodge a complaint with:</p>
                  <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm'>
                    <p className='font-semibold text-gray-900'>Information Commissioner&rsquo;s Office (ICO)</p>
                    <p className='mt-1'>
                      <a href='https://ico.org.uk' target='_blank' rel='noopener noreferrer' className='text-[#001B38] hover:text-[#6941C6] underline font-medium'>https://ico.org.uk</a>
                    </p>
                    <p className='mt-1'>Telephone: 0303 123 1113</p>
                  </div>
                </div>
              </div>

              {/* 1.16 */}
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>1.16 Changes to This Policy</h3>
                <p className='text-gray-700 leading-relaxed'>We may update this Privacy Policy periodically.</p>
              </div>
            </div>
          </section>

          {/* ========== 2. TERMS OF USE ========== */}
          <section id='terms-of-use' className='mb-16 scroll-mt-24'>
            <div className='flex items-center gap-3 mb-6'>
              <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#051C36] text-white text-sm font-bold shrink-0'>2</span>
              <h2 className='text-2xl font-bold text-gray-900 m-0'>Terms of Use</h2>
            </div>

            <div className='space-y-8'>
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.1 Agreement</h3>
                <p className='text-gray-700 leading-relaxed'>These Terms govern your use of BritFiler.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.2 Nature of Service</h3>
                <p className='text-gray-700 leading-relaxed'>BritFiler provides software only and does not provide tax, legal, or financial advice.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.3 No Professional Advice</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>All information provided is for general use only. You are solely responsible for your tax filings.</p>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.4 User Responsibilities</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You agree to:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Provide accurate and complete information</li>
                    <li>Review all submissions before filing</li>
                    <li>Maintain confidentiality of login credentials</li>
                    <li>Comply with all applicable laws</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.5 HMRC Submission Responsibility</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You acknowledge that:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>BritFiler is not the taxpayer</li>
                    <li>BritFiler is not your agent unless explicitly agreed</li>
                    <li>You are fully responsible for all submissions</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.6 Account Security</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You are responsible for:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Securing your account credentials</li>
                    <li>All activity under your account</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.7 Fees and Payments</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Fees must be paid in advance</li>
                    <li>Fees are non-refundable except where required by law</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.8 Service Availability</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We do not guarantee uninterrupted access. We are not liable for downtime caused by:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>HMRC systems</li>
                    <li>Third-party providers</li>
                    <li>Technical failures</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.9 Limitation of Liability</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>To the fullest extent permitted by law, BritFiler shall not be liable for:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Tax penalties or interest</li>
                    <li>Loss of profits</li>
                    <li>Business interruption</li>
                    <li>Data loss</li>
                    <li>Indirect or consequential losses</li>
                  </ul>
                  <p className='font-medium'>Total liability is limited to fees paid in the last 12 months.</p>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.10 Indemnity</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You agree to indemnify BritFiler against any claims arising from:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Incorrect data</li>
                    <li>Misuse of the platform</li>
                    <li>Breach of these Terms</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.11 Suspension and Termination</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We may suspend or terminate your account where:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>You breach these Terms</li>
                    <li>Required by law or regulators</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>2.12 Governing Law</h3>
                <p className='text-gray-700 leading-relaxed'>These Terms are governed by the laws of England and Wales.</p>
              </div>
            </div>
          </section>

          {/* ========== 3. COOKIE POLICY ========== */}
          <section id='cookie-policy' className='mb-16 scroll-mt-24'>
            <div className='flex items-center gap-3 mb-6'>
              <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#051C36] text-white text-sm font-bold shrink-0'>3</span>
              <h2 className='text-2xl font-bold text-gray-900 m-0'>Cookie Policy</h2>
            </div>

            <div className='space-y-8'>
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>3.1 What Are Cookies</h3>
                <p className='text-gray-700 leading-relaxed'>Cookies are small files stored on your device.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>3.2 Types of Cookies</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>We use:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Strictly necessary cookies</li>
                    <li>Analytics cookies</li>
                    <li>Functionality cookies</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>3.3 Legal Basis</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li><strong>Essential cookies:</strong> legitimate interest</li>
                    <li><strong>Analytics cookies:</strong> consent</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>3.4 Third-Party Cookies</h3>
                <p className='text-gray-700 leading-relaxed'>Cookies may be set by third-party providers such as analytics services.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>3.5 Managing Cookies</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You can manage cookies via:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Browser settings</li>
                    <li>Cookie banner</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* ========== 4. USER AGREEMENT ========== */}
          <section id='user-agreement' className='mb-16 scroll-mt-24'>
            <div className='flex items-center gap-3 mb-6'>
              <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#051C36] text-white text-sm font-bold shrink-0'>4</span>
              <h2 className='text-2xl font-bold text-gray-900 m-0'>User Agreement</h2>
            </div>

            <div className='space-y-8'>
              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.1 Account Registration</h3>
                <p className='text-gray-700 leading-relaxed'>You must provide accurate information when creating an account.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.2 Use of Platform</h3>
                <p className='text-gray-700 leading-relaxed'>You agree to use the platform lawfully and responsibly.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.3 Tax Responsibility</h3>
                <p className='text-gray-700 leading-relaxed'>You are solely responsible for all tax filings and submissions.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.4 Agent Authorisation</h3>
                <p className='text-gray-700 leading-relaxed'>Where acting as an agent, you must ensure proper HMRC authorisation is in place.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.5 Prohibited Activities</h3>
                <div className='space-y-3 text-gray-700 leading-relaxed'>
                  <p>You must not:</p>
                  <ul className='list-disc pl-6 space-y-2'>
                    <li>Submit false information</li>
                    <li>Attempt unauthorised access</li>
                    <li>Use the platform for fraudulent purposes</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.6 Termination</h3>
                <p className='text-gray-700 leading-relaxed'>We may suspend or terminate access for violations.</p>
              </div>

              <div>
                <h3 className='text-xl font-semibold text-gray-900 mb-3'>4.7 Governing Law</h3>
                <p className='text-gray-700 leading-relaxed'>This agreement is governed by the laws of England and Wales.</p>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
